// src/components/sops/MySops.js — sopread-7e4c2a93 (SOPs Library, Seam 3)
//
// My SOPs, for everyone in a company whose switch is on: "To read" (overdue first, then soonest due)
// and "All my SOPs". The list on the left (above, at phone width), the selected SOP on the right:
// Open / Download, then the confirm area — tap SOPs: "I've read and understand this"; sign SOPs: the
// typed name, which must match the name on the person's Spatchy account. The button stays grey until
// the server has handed them this exact version (openSop stamps lastOpenedVersionId), and the SERVER
// checks the same thing again (confirmSopRead) — the screen only mirrors it.
// READS: none of its own — SopsPage loads the person's own access records with ONE query and passes
// them in; after Open / Download / Confirm it reloads that same query.
// sopremind-9c4e7a21 (Seam 4) — openId (/sops?open=<sopId>, from a bell or an email) selects that SOP;
// one not in To read opens All my SOPs on it. A re-read owed says so, and needs a fresh open first.
// leadhand-9cb21d86 (Seam 5) — a Lead Hand also sees "Who's read it" under the confirm area (SopReaders);
// everyone else's screen is unchanged.
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BookOpen, Download, ExternalLink, FileText } from 'lucide-react';
import { Button, Notice, OPENED_NOTE, downloadedNote, inputClass } from './SopUi';
import { confirmRead, downloadSop, openSopInNewTab, friendlyError } from './sopsApi';
import SopReaders from './SopReaders';
import {
    BADGE_CLASS, allMyRows, confirmedText, deadlineDayText, hasOpenedCurrent, isReadFor, namesMatch, readingState,
    recordVersionId, toReadRows, SOPS_PATHS, SOP_OPEN_PARAM,
} from '../../utils/sopsUi';

const EMPTY_TEXT = 'No SOPs have been shared with you yet.';

function Badge({ state }) {
    return <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${BADGE_CLASS[state.kind]}`}>{state.label}</span>;
}

function Row({ rec, state, active, onPick }) {
    return (
        <li>
            <button type="button" onClick={onPick} aria-current={active ? 'true' : undefined}
                className={`flex w-full items-start gap-3 px-3 py-3 text-left ${active ? 'bg-[#5F4EC9]/10' : 'hover:bg-gray-50'}`}>
                <FileText size={20} className={`mt-0.5 flex-shrink-0 ${active ? 'text-[#5F4EC9]' : 'text-gray-400'}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                    <span className={`block break-words text-sm font-medium ${active ? 'text-[#5F4EC9]' : 'text-[#12192D]'}`}>{rec.title || 'Untitled SOP'}</span>
                    <span className="block text-xs text-gray-500">{rec.folderName || 'No folder'} · version {rec.currentVersionNumber || 1}</span>
                </span>
                <Badge state={state} />
            </button>
        </li>
    );
}

function Panel({ rec, state, accountName, onChanged, onConfirmed, showReaders = false }) {
    const [notice, setNotice] = useState(null);
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    const [openedHere, setOpenedHere] = useState(false); // shown straight away; the reload brings the server's stamp
    useEffect(() => { setNotice(null); setTyped(''); setOpenedHere(false); }, [rec.sopId, rec.currentVersionId]);

    const versionId = recordVersionId(rec);
    const versionNumber = rec.currentVersionNumber || 1;
    const mustRead = rec.mustRead === true;
    const read = isReadFor(rec);
    const sign = rec.confirmType === 'sign';
    const opened = openedHere || hasOpenedCurrent(rec);
    const nameOk = namesMatch(typed, accountName);
    const canConfirm = !!versionId && opened && (!sign || nameOk) && !busy;

    const open = async () => {
        setNotice(null);
        try { await openSopInNewTab(rec.sopId); setOpenedHere(true); setNotice({ tone: 'info', text: OPENED_NOTE }); onChanged(); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be opened. Please try again.') }); }
    };
    const download = async () => {
        setNotice(null);
        try { const res = await downloadSop(rec.sopId); setOpenedHere(true); setNotice({ tone: 'info', text: downloadedNote(res.fileName) }); onChanged(); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be downloaded. Please try again.') }); }
    };
    const confirm = async () => {
        setBusy(true); setNotice(null);
        try {
            await confirmRead(rec.sopId, versionId, sign ? typed : undefined);
            onConfirmed(rec.sopId);
            await onChanged();
        } catch (e) {
            setNotice({ tone: 'error', text: friendlyError(e, 'That didn’t save. Please try again.') });
            // A new version, or the open wasn't recorded: reload so the screen shows what the server knows.
            if (/new version|Open this version/i.test(String(e && e.message))) { setOpenedHere(false); onChanged(); }
        }
        setBusy(false);
    };

    return (
        <div className="rounded-xl border bg-white p-4 md:p-5">
            <p className="text-sm text-gray-500">{rec.folderName || 'No folder'} · version {versionNumber}</p>
            <h2 className="mt-1 break-words text-xl font-bold text-[#12192D]">{rec.title || 'Untitled SOP'}</h2>

            {mustRead && !read && rec.rereadOwedSince && (
                <div className="mt-3"><Notice tone="warn">Time to re-read this. Open it, then confirm again.</Notice></div>
            )}
            {mustRead && !read && state.kind === 'overdue' && (
                <div className="mt-3"><Notice tone="error">This was due {state.days} day{state.days === 1 ? '' : 's'} ago.</Notice></div>
            )}
            {mustRead && !read && state.kind === 'due' && (
                <div className="mt-3"><Notice tone="warn">You must read this by {deadlineDayText(rec.deadlineAt, state.timeZone)}.</Notice></div>
            )}
            {mustRead && !read && state.kind === 'toRead' && (
                <div className="mt-3"><Notice tone="warn">You must read this.</Notice></div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="primary" onClick={open}><ExternalLink size={16} /> Open</Button>
                <Button onClick={download}><Download size={16} /> Download</Button>
            </div>
            {notice && <div className="mt-3"><Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice></div>}

            {mustRead && (
                <div className="mt-5 border-t pt-4">
                    {read ? (
                        <Notice tone="ok">{confirmedText(rec)}</Notice>
                    ) : sign ? (
                        <div className="space-y-2">
                            <label htmlFor={`sop-sign-${rec.sopId}`} className="block text-sm font-medium text-[#12192D]">Type your full name to sign</label>
                            <input id={`sop-sign-${rec.sopId}`} className={inputClass} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" maxLength={150} />
                            <p className="text-xs text-gray-500">Must match the name on your Spatchy account: {accountName || '(no name on your account — ask your manager to add it)'}.</p>
                            <Button variant="primary" onClick={confirm} disabled={!canConfirm}>{busy ? 'Saving…' : 'Sign: I’ve read and understand this'}</Button>
                            {!opened && <p className="text-xs text-gray-500">Open this version first.</p>}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Button variant="primary" onClick={confirm} disabled={!canConfirm}>{busy ? 'Saving…' : 'I’ve read and understand this'}</Button>
                            {!opened && <p className="text-xs text-gray-500">Open this version first.</p>}
                        </div>
                    )}
                </div>
            )}
            {!mustRead && <p className="mt-4 text-sm text-gray-500">Read this as needed — there’s nothing to confirm.</p>}
            {showReaders && <SopReaders sopId={rec.sopId} versionId={versionId} readKey={rec.lastConfirmedVersionId || null} timeZone={state.timeZone} />}
        </div>
    );
}

export default function MySops({ tab, tabBar, records, status, error, reload, accountName, timeZone, openId = null, showReaders = false }) {
    const [selectedId, setSelectedId] = useState(openId);
    useEffect(() => { if (openId) setSelectedId(openId); }, [openId]); // sopremind-9c4e7a21
    const [keep, setKeep] = useState(() => new Set()); // confirmed on this visit — stay in "To read" for now
    const now = new Date();
    const rows = useMemo(() => (tab === 'all' ? allMyRows(records) : toReadRows(records, keep)), [tab, records, keep]);
    const anyShared = useMemo(() => allMyRows(records).length > 0, [records]);
    const selected = rows.find((r) => r.sopId === selectedId) || rows[0] || null;
    // sopremind-9c4e7a21 — a linked SOP that isn't in To read (read already, or "can see" only): All my SOPs.
    if (openId && tab !== 'all' && status === 'done' && !rows.some((r) => r.sopId === openId) && allMyRows(records).some((r) => r.sopId === openId)) {
        return <Navigate to={`${SOPS_PATHS.all}?${SOP_OPEN_PARAM}=${encodeURIComponent(openId)}`} replace />;
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="mb-3 flex items-center gap-2">
                <BookOpen size={24} className="text-[#5F4EC9]" />
                <h1 className="text-2xl font-bold text-[#12192D]">SOPs</h1>
            </div>
            {tabBar}
            {status === 'error' && <div className="mt-4"><Notice tone="error">{error} <button type="button" className="ml-1 underline" onClick={reload}>Try again</button></Notice></div>}
            {status === 'loading' && <p className="p-12 text-center text-gray-500 animate-pulse">Loading…</p>}
            {status === 'done' && !anyShared && <p className="mt-6 rounded-xl border bg-white p-8 text-center text-sm text-gray-500">{EMPTY_TEXT}</p>}
            {status === 'done' && anyShared && (
                <>
                    {tab !== 'all' && <p className="mt-4 text-sm text-gray-600">Your manager needs you to read these. Open each one, then confirm.</p>}
                    {rows.length === 0 ? (
                        <p className="mt-4 rounded-xl border bg-white p-8 text-center text-sm text-gray-500">You’re all caught up — nothing to read right now.</p>
                    ) : (
                        <div className="mt-4 flex flex-col gap-4 md:flex-row">
                            <ul className="w-full self-start divide-y overflow-hidden rounded-xl border bg-white md:w-80 md:flex-shrink-0">
                                {rows.map((r) => (
                                    <Row key={r.sopId} rec={r} state={readingState(r, now, timeZone)} active={selected && selected.sopId === r.sopId} onPick={() => setSelectedId(r.sopId)} />
                                ))}
                            </ul>
                            <div className="min-w-0 flex-1">
                                {selected && <Panel key={selected.sopId} rec={selected} state={{ ...readingState(selected, now, timeZone), timeZone }} accountName={accountName} onChanged={reload} onConfirmed={(id) => setKeep((k) => new Set([...k, id]))} showReaders={showReaders} />}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
